import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Persistent, non-dismissible prompt when an update has finished downloading.
 * - `sidebar`: above the user panel when logged in (supports collapsed rail).
 * - `floating`: fixed bottom-left above the status bar when logged out.
 */
function UpdateReadyBanner({ variant = "sidebar", collapsed = false, className }) {
  const [releaseName, setReleaseName] = useState("");
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    let isUnmounted = false;

    const applyState = (payload) => {
      if (isUnmounted || !payload) {
        return;
      }
      const status = String(payload.status || "");
      if (status === "downloaded") {
        setVisible(true);
        setInstalling(false);
        setReleaseName(String(payload.releaseName || "").trim());
        return;
      }
      if (status === "installing") {
        setVisible(true);
        setInstalling(true);
        if (payload.releaseName) {
          setReleaseName(String(payload.releaseName).trim());
        }
        return;
      }
      setVisible(false);
      setInstalling(false);
    };

    const syncInitial = async () => {
      const state = await window.electronAPI?.getUpdaterState?.();
      applyState(state);
    };

    void syncInitial();

    const unsubscribe = window.electronAPI?.onUpdaterStatus?.(applyState);
    return () => {
      isUnmounted = true;
      unsubscribe?.();
    };
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    const result = await window.electronAPI?.quitAndInstallUpdate?.();
    if (!result?.ok) {
      setInstalling(false);
      toast.error(result?.reason || "Could not start update installation.");
    }
  };

  if (!visible) {
    return null;
  }

  const subtitle = releaseName ? `${releaseName} · restart to install` : "Restart to install";

  if (variant === "floating") {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "pointer-events-auto fixed bottom-12 left-3 z-50 flex max-w-sm items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 shadow-lg",
          className,
        )}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
          <Download className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-sm font-medium text-foreground">Update ready</p>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <Button
          type="button"
          size="sm"
          className="shrink-0"
          onClick={handleInstall}
          disabled={installing}
        >
          {installing ? "Installing…" : "Update"}
        </Button>
      </div>
    );
  }

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              role="status"
              aria-live="polite"
              aria-label={installing ? "Installing update" : `Update ready. ${subtitle}`}
              className={cn(
                "flex w-full items-center justify-center rounded-md border border-border bg-card p-2 text-primary transition-colors hover:bg-accent disabled:opacity-50",
                className,
              )}
              onClick={handleInstall}
              disabled={installing}
            >
              <Download className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {installing ? "Installing…" : `Update ready — ${subtitle}`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-2.5 rounded-md border border-border bg-card px-2.5 py-2",
        className,
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
        <Download className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-xs font-semibold text-foreground">Update ready</p>
        <p className="truncate text-[10px] text-muted-foreground">{subtitle}</p>
      </div>
      <Button
        type="button"
        size="ssm"
        className="shrink-0"
        onClick={handleInstall}
        disabled={installing}
      >
        {installing ? "…" : "Update"}
      </Button>
    </div>
  );
}

export default UpdateReadyBanner;
